/**
 * Payment API client — wrappers for `/v1/payments/*` endpoints.
 *
 * Backend: `internal/payment/http_app/payments.go` (PR #112, commit 4850575).
 *
 * Routes:
 *   POST /v1/payments              — initiate payment (returns tbank_payment_url)
 *   GET  /v1/payments              — list history (?from=&to= RFC3339)
 *   GET  /v1/payments/{id}         — payment detail
 *
 * The T-Bank cashbox callback (`POST /v1/cashbox/webhook`) is server-to-server
 * and intentionally NOT exposed here.
 */
import { customFetch } from './openapi-fetch.js';
import type {
  Payment,
  PaymentListResponse,
  InitiatePaymentRequest,
} from '@hsc/types';

/**
 * Initiate a payment via the T-Bank cashbox.
 *
 * Backend requires the `Idempotency-Key` header — pass a stable client-side
 * UUID so the same intent is deduplicated server-side (re-clicking the pay
 * button replays the same request → returns 200 with the same Payment).
 *
 * On success, redirect the user to `payment.tbank_payment_url` via
 * `window.location.assign(url)` (see `redirectToTbank` below).
 */
export async function initiatePayment(
  req: InitiatePaymentRequest,
  idempotencyKey: string,
): Promise<Payment> {
  return customFetch<Payment>({
    url: '/payments',
    method: 'POST',
    data: req,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** List payments in [from, to] (RFC3339). Defaults to last 90 days on the server. */
export async function listPayments(opts?: {
  from?: string;
  to?: string;
}): Promise<PaymentListResponse> {
  return customFetch<PaymentListResponse>({
    url: '/payments',
    method: 'GET',
    params: {
      from: opts?.from,
      to: opts?.to,
    },
  });
}

/** Payment detail. */
export async function getPayment(id: string): Promise<Payment> {
  return customFetch<Payment>({
    url: `/payments/${encodeURIComponent(id)}`,
    method: 'GET',
  });
}

/**
 * Redirect the browser to the T-Bank payment page.
 *
 * Encapsulated so tests can stub `window.location.assign` cleanly. In Node
 * test envs without a `window`, this is a no-op.
 */
export function redirectToTbank(url: string): void {
  if (typeof window === 'undefined') return;
  window.location.assign(url);
}
